/**
 * Google Analytics events — single source of truth. snake_case, since GA4 event
 * names must be letters/numbers/underscores (no spaces). Used with `trackEvent`.
 */
export const ANALYTICS_EVENTS = {
  QUIZ_CONFIG_MISSING: "quiz_config_missing",
  PURCHASE_PAGE_VIEWED: "purchase_page_viewed",
  // GA4-standard names where one exists (purchase / begin_checkout / sign_up).
  BEGIN_CHECKOUT: "begin_checkout",
  PAYMENT_METHOD_SELECTED: "payment_method_selected",
  PURCHASE: "purchase",
  PAYMENT_FAILED: "payment_failed",
  SIGN_IN_VIEWED: "sign_in_viewed",
  SIGN_UP: "sign_up",
  SUCCESS_PAGE_VIEWED: "success_page_viewed",
  PAGE_VIEW: "page_view",
  COOKIE_CONSENT: "cookie_consent",
  // Advisors / live-chat (ported from lpr-web). snake_case for GA4; the two
  // popup events are shortened to stay under GA4's 40-char event-name limit.
  CHAT_WITH_ADVISOR: "chat_with_advisor",
  CHAT_WITH_RECOMMENDED_ADVISOR: "chat_with_recommended_advisor",
  CHAT_WITH_MOST_ACCURATE_ADVISOR: "chat_with_most_accurate_advisor",
  CHAT_WITH_BEST_IN_LOVE_ADVISOR: "chat_with_best_in_love_advisor",
  CHAT_WITH_TOP_RATED_ADVISOR: "chat_with_top_rated_advisor",
  CHAT_WITH_ADVISOR_OF_THE_DAY: "chat_with_advisor_of_the_day",
  CHAT_WITH_ADVISOR_AND_PRESET: "chat_with_advisor_and_preset",
  CHAT_WITH_ADVISOR_END: "chat_with_advisor_end",
  PAYWALL_SHOW: "paywall_show",
  CHAT_WITH_ADVISOR_POPUP_SHOWN_AFTER_LOGIN: "chat_with_advisor_login_popup_shown",
  CHAT_WITH_ADVISOR_POPUP_CLICK_AFTER_LOGIN: "chat_with_advisor_login_popup_click",

  // ── Named quiz-funnel events ───────────────────────────────────────────────
  // Sent client-side to GA4 verbatim — names are FINAL per the analytics brief
  // (Trello zBddTv3i Astro / fQBxgcBj Soulmate). One name per interstitial slot
  // (a branch shows one of several variants). Do not rename without the brief.

  // Astro Map (35)
  ASTROMAP_QUIZ_LOADED: "Astromap_Quiz_Loaded",
  ASTROMAP_QUIZ_USERS_TRUST: "Astromap_Quiz_Users_Trust",
  ASTROMAP_QUIZ_1GENDER: "Astromap_Quiz_1Gender",
  ASTROMAP_QUIZ_2MOOD: "Astromap_Quiz_2Mood",
  ASTROMAP_QUIZ_3FEEL: "Astromap_Quiz_3Feel",
  ASTROMAP_QUIZ_4WRONG_PLACE: "Astromap_Quiz_4Wrong_Place",
  ASTROMAP_QUIZ_OUTGROWN: "Astromap_Quiz_Outgrown",
  ASTROMAP_QUIZ_5INSIGHTS: "Astromap_Quiz_5Insights",
  ASTROMAP_QUIZ_JOURNEY: "Astromap_Quiz_Journey",
  ASTROMAP_QUIZ_6FELT_ALIVE: "Astromap_Quiz_6Felt_Alive",
  ASTROMAP_QUIZ_7WHAT_MADE_YOU_FEEL: "Astromap_Quiz_7What_Made_You_Feel",
  ASTROMAP_QUIZ_RIGHT_PEOPLE: "Astromap_Quiz_Right_People",
  ASTROMAP_QUIZ_8CURRENT_PLACE: "Astromap_Quiz_8Current_Place",
  ASTROMAP_QUIZ_9LIVE_NOW: "Astromap_Quiz_9Live_Now",
  ASTROMAP_QUIZ_10KEEP_FROM_LEAVING: "Astromap_Quiz_10Keep_From_Leaving",
  ASTROMAP_QUIZ_11PERFECT_PLACE: "Astromap_Quiz_11Perfect_Place",
  ASTROMAP_QUIZ_12LIFESTYLE: "Astromap_Quiz_12Lifestyle",
  ASTROMAP_QUIZ_CALM_WATERS: "Astromap_Quiz_Calm_Waters",
  ASTROMAP_QUIZ_13ENERGY: "Astromap_Quiz_13Energy",
  ASTROMAP_QUIZ_14ELEMENT: "Astromap_Quiz_14Element",
  ASTROMAP_QUIZ_15STATUS: "Astromap_Quiz_15Status",
  ASTROMAP_QUIZ_16STATUS: "Astromap_Quiz_16Status",
  ASTROMAP_QUIZ_17BELIEVE: "Astromap_Quiz_17Believe",
  ASTROMAP_QUIZ_18IDEAL_LOCATION: "Astromap_Quiz_18Ideal_Location",
  ASTROMAP_QUIZ_19HOME: "Astromap_Quiz_19Home",
  ASTROMAP_QUIZ_ANCHOR: "Astromap_Quiz_Anchor",
  ASTROMAP_QUIZ_20BIRTHDAY: "Astromap_Quiz_20Birthday",
  ASTROMAP_QUIZ_21BIRTHPLACE: "Astromap_Quiz_21Birthplace",
  ASTROMAP_QUIZ_MAP: "Astromap_Quiz_Map",
  ASTROMAP_QUIZ_PROCESSING: "Astromap_Quiz_Processing",
  ASTROMAP_QUIZ_EMAIL: "Astromap_Quiz_Email",
  ASTROMAP_PROMOCODE_CLICK: "Astromap_Promocode_Click",
  ASTROMAP_SUB_SCREEN_SHOWN: "Astromap_Sub_Screen_Shown",
  ASTROMAP_SUB_SCREEN_BUTTON_CLICK: "Astromap_Sub_Screen_Button_Click",
  ASTROMAP_SUB_SCREEN_SUB_BUTTON_CLICK: "Astromap_Sub_Screen_Sub_Button_Click",

  // Soulmate Sketch (28)
  SOULMATE_QUIZ_LOADED: "Soulmate_Quiz_Loaded",
  SOULMATE_QUIZ_USERS_TRUST: "Soulmate_Quiz_Users_Trust",
  SOULMATE_QUIZ_1GENDER: "Soulmate_Quiz_1Gender",
  SOULMATE_QUIZ_2PARTNER_GENDER: "Soulmate_Quiz_2Partner_Gender",
  SOULMATE_QUIZ_3SOULMATE_AGE: "Soulmate_Quiz_3Soulmate_Age",
  SOULMATE_QUIZ_4ETHNIC: "Soulmate_Quiz_4Ethnic",
  SOULMATE_QUIZ_5BIRTHDAY: "Soulmate_Quiz_5Birthday",
  SOULMATE_QUIZ_6QUALITIES: "Soulmate_Quiz_6Qualities",
  SOULMATE_QUIZ_NOTED: "Soulmate_Quiz_Noted",
  SOULMATE_QUIZ_7LOOKS: "Soulmate_Quiz_7Looks",
  SOULMATE_QUIZ_8DRIVEN: "Soulmate_Quiz_8Driven",
  SOULMATE_QUIZ_ADVICE: "Soulmate_Quiz_Advice",
  SOULMATE_QUIZ_9REASON: "Soulmate_Quiz_9Reason",
  SOULMATE_QUIZ_10DIFFICULT: "Soulmate_Quiz_10Difficult",
  SOULMATE_QUIZ_STRUGGLE: "Soulmate_Quiz_Struggle",
  SOULMATE_QUIZ_11LANGUAGE: "Soulmate_Quiz_11Language",
  SOULMATE_QUIZ_12FUTURE: "Soulmate_Quiz_12Future",
  SOULMATE_QUIZ_13ENERGY: "Soulmate_Quiz_13Energy",
  SOULMATE_QUIZ_14BELIEVE: "Soulmate_Quiz_14Believe",
  SOULMATE_QUIZ_15EXPERIENCE: "Soulmate_Quiz_15Experience",
  SOULMATE_QUIZ_PHOTO: "Soulmate_Quiz_Photo",
  SOULMATE_QUIZ_PHOTO_PROCESSING: "Soulmate_Quiz_Photo_Processing",
  SOULMATE_QUIZ_EMAIL: "Soulmate_Quiz_Email",
  SOULMATE_QUIZ_CONFIRM: "Soulmate_Quiz_Confirm",
  SOULMATE_QUIZ_PROCESSING: "Soulmate_Quiz_Processing",
  SOULMATE_SUB_SCREEN_SHOWN: "Soulmate_Sub_Screen_Shown",
  SOULMATE_SUB_SCREEN_BUTTON_CLICK: "Soulmate_Sub_Screen_Button_Click",
  SOULMATE_SUB_SCREEN_SUB_BUTTON_CLICK: "Soulmate_Sub_Screen_Sub_Button_Click",

  // Quiz Simplified (15)
  SIMPLIFIED_QUIZ_LOADED: "Simplified_Quiz_Loaded",
  SIMPLIFIED_QUIZ_NAME: "Simplified_Quiz_Name",
  SIMPLIFIED_QUIZ_GENDER: "Simplified_Quiz_Gender",
  SIMPLIFIED_QUIZ_BIRTHDAY: "Simplified_Quiz_Birthday",
  SIMPLIFIED_QUIZ_BIRTHTIME: "Simplified_Quiz_Birthtime",
  SIMPLIFIED_QUIZ_STATUS: "Simplified_Quiz_Status",
  SIMPLIFIED_QUIZ_PHOTO_LEFT: "Simplified_Quiz_Photo_Left",
  SIMPLIFIED_QUIZ_PHOTO_LEFT_PROCESSED: "Simplified_Quiz_Photo_Left_Processed",
  SIMPLIFIED_QUIZ_PHOTO_RIGHT: "Simplified_Quiz_Photo_Right",
  SIMPLIFIED_QUIZ_PHOTO_RIGHT_PROCESSED: "Simplified_Quiz_Photo_Right_Processed",
  SIMPLIFIED_QUIZ_PHOTO_PROCESSING: "Simplified_Quiz_Photo_Processing",
  SIMPLIFIED_QUIZ_PRESUB: "Simplified_Quiz_Presub",
  SIMPLIFIED_QUIZ_EMAIL: "Simplified_Quiz_Email",
  SIMPLIFIED_SUB_SCREEN_SHOWN: "Simplified_Sub_Screen_Shown",
  SIMPLIFIED_SUB_SCREEN_SUB_BUTTON_CLICK: "Simplified_Sub_Screen_Sub_Button_Click",

  // Love Compatibility (21)
  COMPAT_QUIZ_LOADED: "Compat_Quiz_Loaded",
  COMPAT_QUIZ_START: "Compat_Quiz_Start",
  COMPAT_QUIZ_1NAME: "Compat_Quiz_1Name",
  COMPAT_QUIZ_2BIRTHDAY: "Compat_Quiz_2Birthday",
  COMPAT_QUIZ_3BIRTHTIME: "Compat_Quiz_3Birthtime",
  COMPAT_QUIZ_4GENDER: "Compat_Quiz_4Gender",
  COMPAT_QUIZ_5FEEL: "Compat_Quiz_5Feel",
  COMPAT_QUIZ_EXPLORE: "Compat_Quiz_Explore",
  COMPAT_QUIZ_6PARTNER_NAME: "Compat_Quiz_6Partner_Name",
  COMPAT_QUIZ_7PARTNER_BIRTHDAY: "Compat_Quiz_7Partner_Birthday",
  COMPAT_QUIZ_8PARTNER_BIRTHTIME: "Compat_Quiz_8Partner_Birthtime",
  COMPAT_QUIZ_9PARTNER_GENDER: "Compat_Quiz_9Partner_Gender",
  COMPAT_QUIZ_10GOAL: "Compat_Quiz_10Goal",
  COMPAT_QUIZ_11PROBLEM: "Compat_Quiz_11Problem",
  COMPAT_QUIZ_INSIGHT: "Compat_Quiz_Insight",
  COMPAT_QUIZ_PROCESSING: "Compat_Quiz_Processing",
  COMPAT_QUIZ_EMAIL: "Compat_Quiz_Email",
  COMPAT_QUIZ_PRESUBSCRIPTION: "Compat_Quiz_Presubscription",
  COMPAT_SUB_SCREEN_SHOWN: "Compat_Sub_Screen_Shown",
  COMPAT_SUB_SCREEN_BUTTON_CLICK: "Compat_Sub_Screen_Button_Click",
  COMPAT_SUB_SCREEN_SUB_BUTTON_CLICK: "Compat_Sub_Screen_Sub_Button_Click",

  // Review modal (first webapp entry)
  REVIEW_MODAL_SHOWN: "review_modal_shown",
  REVIEW_MODAL_RATING_SUBMITTED: "review_modal_rating_submitted",
  REVIEW_MODAL_LEAVE_REVIEW_CLICK: "review_modal_leave_review_click",
  REVIEW_MODAL_FEEDBACK_SUBMITTED: "review_modal_feedback_submitted",
  REVIEW_MODAL_CLOSED: "review_modal_closed",

  // Aura Scan
  AURA_QUIZ_LOADED: "Aura_Quiz_Loaded",
  AURA_QUIZ_USERS_TRUST: "Aura_Quiz_Users_Trust",
  AURA_QUIZ_1GENDER: "Aura_Quiz_1Gender",
  AURA_QUIZ_2EXPLORE: "Aura_Quiz_2Explore",
  AURA_QUIZ_FEATURES: "Aura_Quiz_Features",
  AURA_QUIZ_3BIRTHDAY: "Aura_Quiz_3Birthday",
  AURA_QUIZ_4BIRTHTIME: "Aura_Quiz_4Birthtime",
  AURA_QUIZ_5BIRTHPLACE: "Aura_Quiz_5Birthplace",
  AURA_QUIZ_6ATMOSPHERE: "Aura_Quiz_6Atmosphere",
  AURA_QUIZ_7ANIMALS: "Aura_Quiz_7Animals",
  AURA_QUIZ_8PRESENCE: "Aura_Quiz_8Presence",
  AURA_QUIZ_ARCHETYPE: "Aura_Quiz_Archetype",
  AURA_QUIZ_9OPEN: "Aura_Quiz_9Open",
  AURA_QUIZ_10CROWD: "Aura_Quiz_10Crowd",
  AURA_QUIZ_11STRUGGLING: "Aura_Quiz_11Struggling",
  AURA_QUIZ_12TENSION: "Aura_Quiz_12Tension",
  AURA_QUIZ_13PURPOSE: "Aura_Quiz_13Purpose",
  AURA_QUIZ_14ELEMENT: "Aura_Quiz_14Element",
  AURA_QUIZ_15SENSE: "Aura_Quiz_15Sense",
  AURA_QUIZ_INTUITION: "Aura_Quiz_Intuition",
  AURA_QUIZ_16NATURE: "Aura_Quiz_16Nature",
  AURA_QUIZ_17WEATHER: "Aura_Quiz_17Weather",
  AURA_QUIZ_18SIGNS: "Aura_Quiz_18Signs",
  AURA_QUIZ_19DREAMS: "Aura_Quiz_19Dreams",
  AURA_QUIZ_RADAR: "Aura_Quiz_Radar",
  AURA_QUIZ_20RECHARGE: "Aura_Quiz_20Recharge",
  AURA_QUIZ_21ACTIVITY: "Aura_Quiz_21Activity",
  AURA_QUIZ_22PRACTICES: "Aura_Quiz_22Practices",
  AURA_QUIZ_23PHENOMENON: "Aura_Quiz_23Phenomenon",
  AURA_QUIZ_24SENSATIONS: "Aura_Quiz_24Sensations",
  AURA_QUIZ_PALM_INTRO: "Aura_Quiz_Palm_Intro",
  AURA_QUIZ_PHOTO: "Aura_Quiz_Photo",
  AURA_QUIZ_PROCESSING: "Aura_Quiz_Processing",
  AURA_QUIZ_EMAIL: "Aura_Quiz_Email",
  AURA_SUB_SCREEN_SHOWN: "Aura_Sub_Screen_Shown",
  AURA_SUB_SCREEN_BUTTON_CLICK: "Aura_Sub_Screen_Button_Click",
  AURA_SUB_SCREEN_SUB_BUTTON_CLICK: "Aura_Sub_Screen_Sub_Button_Click",
} as const

export type AnalyticsEventName =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS]

/**
 * Attribution payload sent to the subscriptions backend.
 * Field names mirror the Subtrack/origene contract exactly — only these keys
 * are recognized server-side. Falsy values are stripped before sending.
 */
export interface AttributionData {
  project: string | null
  campaign_id: string | null
  campaign_name: string | null
  adset_id: string | null
  adset_name: string | null
  ad_id: string | null
  ad_name: string | null
  media_source: string | null
  config: string | null
  fb_pixel: string | null
  fbc: string | null
  fbp: string | null
  referer: string | null
  event_source_url: string | null
  client_user_agent: string | null
  idfm: string | null
  /** Enriched asynchronously from the geo lookup. */
  country_code: string | null
  client_ip_address: string | null
}

export interface AnalyticsPayload {
  eventName: AnalyticsEventName
  userId?: string
  anonymousId?: string
  sessionId?: string
  productId?: string
  /** Subscription/order id — sent to GA4 as `transaction_id` (purchase dedup). */
  transactionId?: string
  price?: number
  currency?: string
  paymentMethod?: "card" | "apple_pay" | "google_pay"
  attribution?: AttributionData
  metadata?: Record<string, unknown>
}

export interface AnalyticsProvider {
  name: string
  init(): void
  track(event: AnalyticsEventName, payload: AnalyticsPayload): void
  page(path: string): void
}
